/**
 * @ethisyscore/host-ui-externals/mock
 *
 * Stub implementations of every host-ui-externals export for use in
 * plugin CI, Storybook, and local development (when no host is running).
 * Renders plain semantic HTML with the correct `data-testid` attributes so
 * plugin tests can assert on structure without a real MUI/Gogo tree.
 *
 * Uses `createElement` (not JSX) so this stays a plain `.ts` module — no
 * `jsx` tsconfig setting required for the package's type-check/build.
 */
import { createElement, type FC, type ReactNode } from "react";
import type { GogoThemeProviderProps } from "./index";
export type { GogoTheme, GogoMode, GogoThemeProviderProps, GogoMenuItem } from "./index";
export { GOGO_EXTERNALS_SPECIFIER_MAP } from "./index";

/**
 * Mock `GogoThemeProvider` — simply renders `children` inside a `<div>`.
 * Does NOT apply any MUI theme; sufficient for unit/smoke tests.
 */
export const GogoThemeProvider: FC<GogoThemeProviderProps> = ({ children }) =>
  createElement("div", { "data-testid": "mock-gogo-theme-provider" }, children as ReactNode);

/**
 * Mock `useGogoTheme` — returns stable no-op values.
 */
export function useGogoTheme() {
  return {
    theme: "theme-blue" as const,
    setTheme: (_: string) => {},
    mode: "light" as const,
    isDarkMode: false,
    direction: "ltr" as const,
    setMode: (_: string) => {},
  };
}
